import { type RouteObject } from 'react-router-dom'

import { lazyPage } from '@/shared/lib/chunkLoad'

import { AdminLayout } from '../components/AdminLayout'
import { RequireAdmin } from '../guards/RequireAdmin'

const AdminDashboardPage = lazyPage(() =>
  import('../pages/AdminDashboardPage').then((m) => ({ default: m.AdminDashboardPage })),
)
const AdminStatsPage = lazyPage(() =>
  import('../pages/AdminStatsPage').then((m) => ({ default: m.AdminStatsPage })),
)
const AdminReportsPage = lazyPage(() =>
  import('../pages/AdminReportsPage').then((m) => ({ default: m.AdminReportsPage })),
)
const AdminReportDetailPage = lazyPage(() =>
  import('../pages/AdminReportDetailPage').then((m) => ({ default: m.AdminReportDetailPage })),
)
const AdminAuditPage = lazyPage(() =>
  import('../pages/AdminAuditPage').then((m) => ({ default: m.AdminAuditPage })),
)
const AdminUsersPage = lazyPage(() =>
  import('../pages/AdminUsersPage').then((m) => ({ default: m.AdminUsersPage })),
)
const AdminUserDetailPage = lazyPage(() =>
  import('../pages/AdminUserDetailPage').then((m) => ({ default: m.AdminUserDetailPage })),
)
const AdminHeroCampaignsPage = lazyPage(() =>
  import('@/features/hero-campaigns/admin/AdminHeroCampaignsPage').then((m) => ({
    default: m.AdminHeroCampaignsPage,
  })),
)
const AdminVenuesPage = lazyPage(() =>
  import('@/features/stores/admin/AdminVenuesPage').then((m) => ({
    default: m.AdminVenuesPage,
  })),
)
const DebugCronsPage = lazyPage(() => import('@/features/debug/pages/DebugCronsPage'))

/**
 * Sub-árbol de rutas del shell admin. Comparte un único wrapper
 * `<RequireAdmin><AdminLayout/></RequireAdmin>` para que todas las páginas
 * — reports, audit y debug-crons — rendericen DENTRO del sidebar admin.
 *
 * Se inyecta en el router root como hijo de MainLayout (vive bajo
 * SiteHeader) y del SuspenseShell (fallback global de lazy).
 *
 * El gate de rol vive en `RequireAdmin` (NotFoundPage si no es admin,
 * loader si el auth store aún está hidratando).
 *
 * `/debug/crons` conserva su URL histórica (no se renombra a
 * `/admin/debug/crons`) para no romper bookmarks ni el link directo
 * desde el dropdown del header.
 */
export const adminRoutes: RouteObject = {
  element: (
    <RequireAdmin>
      <AdminLayout />
    </RequireAdmin>
  ),
  children: [
    {
      path: '/admin',
      children: [
        { index: true, element: <AdminDashboardPage /> },
        { path: 'stats', element: <AdminStatsPage /> },
        { path: 'reports', element: <AdminReportsPage /> },
        { path: 'reports/:id', element: <AdminReportDetailPage /> },
        { path: 'users', element: <AdminUsersPage /> },
        { path: 'users/:username', element: <AdminUserDetailPage /> },
        { path: 'audit', element: <AdminAuditPage /> },
        { path: 'hero-campaigns', element: <AdminHeroCampaignsPage /> },
        { path: 'locales', element: <AdminVenuesPage /> },
      ],
    },
    { path: '/debug/crons', element: <DebugCronsPage /> },
  ],
}
